import type * as preact from 'preact';
import type { Sct, SignedEntry } from '../../core/rfc6962/types';
import { leafIndexFromExtensions } from '../../core/rfc6962/sct';
import { hashAlgorithmName, signatureAlgorithmName } from '../../core/tls/vectors';
import type { SctVerdict } from '../../core/sct/embedded';
import { TransportError } from '../../core/transport/errors';
import { withFrom } from '../../router';
import { fmtInt } from '../format';
import { useAsync } from '../hooks/useAsync';
import type { CheckState } from '../hooks/useInclusionChecks';
import { logPath } from '../nav';
import { locateSctEntry } from '../sctLinks';
import { registry } from '../state/logs';
import { Badge } from './Badge';
import { Hash } from './Hash';
import { Icon } from './Icon';
import { Spinner } from './Spinner';
import { TimeStamp } from './TimeStamp';
import { KindBadge } from './LogBadges';

export type { SctVerdict };

export interface SctTableProps {
  scts: Sct[];
  verdicts?: SctVerdict[];
  inclusionLinks?: Array<string | undefined>;
  /** The signed_entry the SCTs cover (precert entry for embedded SCTs, x509 for TLS-delivered ones). */
  entry?: SignedEntry | undefined;
  /** Per-row override of `entry` when the SCTs cover different entries. */
  entries?: Array<SignedEntry | undefined>;
  /** Results of checkInclusion() per SCT; adds the "In the log?" column. */
  checks?: CheckState[];
  /** Per-row note under the log name, e.g. "TLS extension". */
  labels?: Array<string | undefined>;
}

/**
 * Lists SCTs with the issuing log resolved from the log list, the log entry they were issued for
 * (located live when the entry is known), caller-supplied signature verdicts, and — when `checks`
 * is given — whether the entry is provably in the log right now.
 */
export function SctTable({ scts, verdicts, inclusionLinks, entry, entries, checks, labels }: SctTableProps) {
  const reg = registry.value;
  if (scts.length === 0) return <p class="muted">No SCTs.</p>;
  return (
    <div class="table-wrap">
      <table class="table">
        <thead>
          <tr>
            <th>Log</th>
            <th>Timestamp</th>
            <th>Logged entry</th>
            <th class="col-sig">Signature</th>
            {verdicts && <th>Check</th>}
            {checks && <th>In the log?</th>}
            {inclusionLinks && <th></th>}
          </tr>
        </thead>
        <tbody>
          {scts.map((s, i) => {
            const log = reg.byLogId(s.logId);
            const v = verdicts?.[i];
            const c = checks?.[i];
            const e = entries ? entries[i] : entry;
            return (
              <tr key={i}>
                <td>
                  {log ? (
                    <>
                      <a href={withFrom(logPath(log))}>{log.description}</a> <KindBadge kind={log.kind} />
                      <div class="tiny muted">{log.operator}{labels?.[i] && <> · {labels[i]}</>}</div>
                    </>
                  ) : (
                    <>
                      <span class="muted">unknown log</span>
                      <div class="tiny"><Hash value={s.logId} short /></div>
                    </>
                  )}
                </td>
                <td class="small nowrap"><TimeStamp ms={s.timestamp} relative={false} /></td>
                <td class="nowrap"><EntryCell sct={s} entry={e} check={c} /></td>
                <td class="col-sig small muted nowrap">{signatureAlgorithmName(s.signature.signatureAlgorithm)}/{hashAlgorithmName(s.signature.hashAlgorithm)} · {s.signature.signature.length} B</td>
                {verdicts && (
                  <td>
                    {v === undefined && <span class="muted">…</span>}
                    {v?.ok === true && <Badge tone="ok"><Icon name="check" size={12} /> valid</Badge>}
                    {v?.ok === false && <><Badge tone="err">INVALID</Badge>{v.error && <div class="tiny">{v.error}</div>}</>}
                    {v?.ok === 'unverifiable' && <><Badge tone="warn" title={v.reason}>cannot verify</Badge><div class="tiny muted">{v.reason}</div></>}
                  </td>
                )}
                {checks && <td><CheckCell check={c} /></td>}
                {inclusionLinks && (
                  <td class="nowrap">
                    {inclusionLinks[i] ? <a class="btn btn-sm" href={withFrom(inclusionLinks[i]!)}><Icon name="shield" /> {checks ? 'Proof' : 'Check inclusion'}</a> : <span class="muted tiny">—</span>}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** Verdict of checkInclusion() for one SCT: a badge plus a short note on one line; details in the tooltip. */
function CheckCell({ check }: { check: CheckState }) {
  if (check === undefined) return <span class="muted tiny">not checked</span>;
  if (check === 'running') return <Spinner label="checking…" />;
  const cell = (badge: preact.ComponentChildren, note: string, title: string) => (
    <span class="sct-status" title={title}>{badge}<span class="tiny muted">{note}</span></span>
  );
  switch (check.status) {
    case 'included':
      return cell(
        <Badge tone="ok"><Icon name="check" size={12} /> included</Badge>,
        check.leafMatched === 'unknown' ? 'entry exists' : 'proof verified',
        `Inclusion proof for entry #${fmtInt(check.index)} verified against the ${check.headVerified ? 'signed' : 'unsigned'} tree head of ${fmtInt(check.treeSize)} entries${check.leafMatched === 'unknown' ? ' (leaf not compared with this certificate — needs the issuer)' : ''}`,
      );
    case 'invalid':
      return cell(<Badge tone="err">NOT VERIFIED</Badge>, 'proof failed', check.detail);
    case 'not-found':
      return check.withinMmd
        ? cell(<Badge tone="warn">not yet</Badge>, 'within merge delay', 'The log has no entry for this SCT yet; logs may take up to their maximum merge delay to include it')
        : cell(<Badge tone="err">not found</Badge>, 'no such entry', 'The log has no entry for this SCT although its merge delay has passed');
    case 'blocked':
      return cell(<Badge tone="warn">can’t ask</Badge>, 'blocked (CORS)', check.error.hint ?? 'The log does not send CORS headers — use a proxy or the desktop app');
    case 'no-entry':
      return cell(<Badge tone="muted">not checkable</Badge>, check.reason.includes('issuer') ? 'needs issuer' : 'no leaf index', check.reason);
    case 'unknown-log':
      return <span class="muted tiny">unknown log</span>;
    case 'error':
      return cell(<Badge tone="err">failed</Badge>, 'lookup error', check.error instanceof Error ? check.error.message : String(check.error));
    case 'timeout':
      return cell(<Badge tone="warn">no answer</Badge>, 'timed out', 'The log did not answer within 45 s — try again later');
  }
}

/** "#index" link to the (pre)certificate entry the SCT covers, resolved live for RFC 6962 logs. */
function EntryCell({ sct, entry, check }: { sct: Sct; entry: SignedEntry | undefined; check: CheckState }) {
  const idx = leafIndexFromExtensions(sct.extensions);
  // An inclusion check already located the entry; only query the log when there is none.
  const fromCheck = check && check !== 'running' && (check.status === 'included' || check.status === 'invalid') && check.index !== undefined ? { log: check.log, index: check.index } : undefined;
  const st = useAsync(async () => (fromCheck || check === 'running' ? undefined : locateSctEntry(sct, entry)), [sct, entry, fromCheck?.index, check === 'running']);
  const r = fromCheck ? { status: 'found' as const, ...fromCheck } : st.value;
  if (r?.status === 'found') {
    return (
      <a href={withFrom(logPath(r.log, `entry/${r.index}`))} title={idx !== undefined ? 'From the leaf_index SCT extension' : 'Located via get-proof-by-hash'}>
        #{fmtInt(r.index)}
      </a>
    );
  }
  if (check === 'running' || st.status === 'loading') return <span class="muted tiny">locating…</span>;
  if (r?.status === 'unknown-log') return <span class="muted tiny">—</span>;
  if (r?.status === 'no-entry') return <span class="muted tiny" title="Needs the issuer certificate to reconstruct the entry">needs issuer</span>;
  if (r?.status === 'error') {
    const e = r.error;
    if (e instanceof TransportError && e.kind === 'cors-blocked') return <span class="muted tiny" title={e.hint}>log blocks browsers</span>;
    if (e instanceof TransportError && e.status === 404) return <span class="muted tiny" title="The log does not (yet) have this leaf — new entries take up to the MMD to appear">not in log yet</span>;
    return <span class="muted tiny" title={e instanceof Error ? e.message : String(e)}>lookup failed</span>;
  }
  return <span class="muted tiny">—</span>;
}
